import { useRef, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useFormContext, type FieldPath } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { adsProvider } from '../../providers/adsProvider';
import { locationProvider } from '../../providers/locationProvider';
import { AdFieldStatusEnum, NotificationTypeEnum } from '../../types/enums';
import type { AdMediaType, AdModel } from '../../types/ads';
import type {
  PropertyAdEditorFormValues,
  PropertyAdInProgressWorkspace,
} from '../../types/propertyAdEditor';
import type { State } from '../../types/states';
import { appSetNotification } from '../../store';

import { MyButton } from '../uiComponents/MyButton';
import { MyDialog } from '../uiComponents/MyDialog';
import { buildPropertyAdEditorEditRequest } from './propertyAdEditorEdit';
import type { PropertyAdEditorMediaIdleResult } from './usePropertyAdEditorMedia';
import {
  usePropertyAdEditorLiveValidation,
  type PropertyAdPublishValidationResult,
} from './usePropertyAdEditorLiveValidation';

const MY_PROPERTIES_ROUTE = '/ads/my';
const UNPUBLISHED_PROPERTIES_ROUTE = `${MY_PROPERTIES_ROUTE}?page=1&status=unpublished`;

type PublishErrorTarget =
  | {
      field: FieldPath<PropertyAdEditorFormValues>;
      sectionTitleId: string;
      type: 'field';
    }
  | {
      sectionTitleId: string;
      type: 'photo';
    };

const PUBLISH_ERROR_TARGETS: readonly PublishErrorTarget[] = [
  { field: 'activityKind', sectionTitleId: 'property-details-title', type: 'field' },
  { field: 'transactionType', sectionTitleId: 'property-details-title', type: 'field' },
  { field: 'propertyKind', sectionTitleId: 'property-details-title', type: 'field' },
  { field: 'price', sectionTitleId: 'pricing-transaction-title', type: 'field' },
  { field: 'sellingPrice', sectionTitleId: 'pricing-transaction-title', type: 'field' },
  { field: 'toSeller', sectionTitleId: 'pricing-transaction-title', type: 'field' },
  {
    field: 'defaultRentFrequency',
    sectionTitleId: 'pricing-transaction-title',
    type: 'field',
  },
  { field: 'priceYearly', sectionTitleId: 'pricing-transaction-title', type: 'field' },
  { field: 'priceMonthly', sectionTitleId: 'pricing-transaction-title', type: 'field' },
  { field: 'priceWeekly', sectionTitleId: 'pricing-transaction-title', type: 'field' },
  { field: 'priceDaily', sectionTitleId: 'pricing-transaction-title', type: 'field' },
  { field: 'locationId', sectionTitleId: 'property-location-title', type: 'field' },
  { field: 'multilisting', sectionTitleId: 'commission-conditions-title', type: 'field' },
  { field: 'beds', sectionTitleId: 'property-specifications-title', type: 'field' },
  { field: 'builtUpArea', sectionTitleId: 'property-specifications-title', type: 'field' },
  {
    field: 'description',
    sectionTitleId: 'property-ad-description-title',
    type: 'field',
  },
  { sectionTitleId: 'upload-photo-title', type: 'photo' },
];

const PUBLISH_ERROR_FIELD_ORDER = PUBLISH_ERROR_TARGETS.flatMap((target) =>
  target.type === 'field' ? [target.field] : []
);

type FinalOperation = 'cancel' | 'draft' | 'publish';

interface PropertyAdEditorActionsBaseProps {
  hasPendingMedia: () => boolean;
  media: AdMediaType[];
  onFinalizingChange?: (isFinalizing: boolean) => void;
  onPhotoPublishErrorChange?: (message: string) => void;
  waitForMediaIdle: () => Promise<PropertyAdEditorMediaIdleResult>;
}

interface PropertyAdEditorCreateActionsProps extends PropertyAdEditorActionsBaseProps {
  flushAutosave: () => Promise<boolean>;
  mode?: 'create';
  stopAutosave: () => void;
  workspace: Pick<PropertyAdInProgressWorkspace, 'id'>;
}

interface PropertyAdEditorEditActionsProps extends PropertyAdEditorActionsBaseProps {
  mode: 'edit';
  propertyAd: AdModel;
  responsibleDefaultUserId?: string;
}

type PropertyAdEditorActionsProps =
  | PropertyAdEditorCreateActionsProps
  | PropertyAdEditorEditActionsProps;

const getErrorMessage = (error: unknown, fallback: string) =>
  error instanceof Error ? error.message : fallback;

export const PropertyAdEditorActions = (props: PropertyAdEditorActionsProps) => {
  const { media, onPhotoPublishErrorChange } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUserId = useSelector(({ auth }: State) => auth.user.id);
  const { getValues } = useFormContext<PropertyAdEditorFormValues>();
  const operationRef = useRef<FinalOperation | null>(null);
  const [operation, setOperation] = useState<FinalOperation | null>(null);
  const [requestError, setRequestError] = useState('');
  const [isPublishConfirmationOpen, setIsPublishConfirmationOpen] = useState(false);
  const [isPublishSuccessOpen, setIsPublishSuccessOpen] = useState(false);
  const [isLocationNoticeOpen, setIsLocationNoticeOpen] = useState(false);
  const [publishedTitle, setPublishedTitle] = useState('');
  const propertyAdId = props.mode === 'edit' ? props.propertyAd.id : props.workspace.id;
  const { validatePublish: validatePublishFields } = usePropertyAdEditorLiveValidation({
    fieldOrder: PUBLISH_ERROR_FIELD_ORDER,
    media,
    onPhotoPublishErrorChange,
  });
  const publishClickPendingRef = useRef(false);

  const beginOperation = (nextOperation: FinalOperation) => {
    if (operationRef.current) return false;
    operationRef.current = nextOperation;
    setOperation(nextOperation);
    setRequestError('');
    return true;
  };

  const finishFailedOperation = () => {
    if (operationRef.current === 'publish') props.onFinalizingChange?.(false);
    operationRef.current = null;
    setOperation(null);
  };

  const showRequestError = (message: string, status?: number) => {
    setRequestError(message);
    dispatch(appSetNotification(NotificationTypeEnum.Error, message, status));
  };

  const savePendingChanges = async () => {
    if (props.mode === 'edit') return true;

    const saved = await props.flushAutosave();
    if (!saved) finishFailedOperation();
    return saved;
  };

  const requestSave = async (status: AdFieldStatusEnum, currentMedia = media) => {
    const response =
      props.mode === 'edit'
        ? await adsProvider.updateAdById(
            props.propertyAd.id,
            buildPropertyAdEditorEditRequest({
              fallbackCreatorId: props.propertyAd.creatorId,
              fromXml: props.propertyAd.fromXml,
              media: currentMedia,
              ...(status === AdFieldStatusEnum.Published
                ? { publishedAt: new Date().toISOString() }
                : {}),
              responsibleDefaultUserId: props.responsibleDefaultUserId,
              status,
              values: getValues(),
            })
          )
        : await adsProvider.updateAdById(props.workspace.id, { status });

    if (!response.ok) {
      showRequestError(response.message || "Couldn't finish the listing", response.status);
      finishFailedOperation();
      return false;
    }

    if (props.mode !== 'edit') props.stopAutosave();
    return true;
  };

  const handleSaveDraft = async () => {
    if (!beginOperation('draft')) return;

    try {
      if (!(await savePendingChanges())) return;
      if (!(await requestSave(AdFieldStatusEnum.Unpublished))) return;

      const values = getValues();
      if (!values.locationId && values.districtId && values.buildingName) {
        try {
          const verificationResponse = await locationProvider.verificationRequest({
            userId: currentUserId,
            propertyAdId,
            districtId: values.districtId,
            propertyName: values.buildingName,
          });

          if (!verificationResponse.ok) {
            dispatch(
              appSetNotification(
                NotificationTypeEnum.Error,
                verificationResponse.message || "Couldn't request location verification",
                verificationResponse.status
              )
            );
          }
        } catch (verificationError) {
          dispatch(
            appSetNotification(
              NotificationTypeEnum.Error,
              getErrorMessage(verificationError, "Couldn't request location verification")
            )
          );
        }
      }

      navigate(UNPUBLISHED_PROPERTIES_ROUTE);
    } catch (requestError) {
      showRequestError(getErrorMessage(requestError, "Couldn't save the listing as a draft"));
      finishFailedOperation();
    }
  };

  const handleCancel = async () => {
    if (!beginOperation('cancel')) return;

    try {
      if (props.mode === 'edit') {
        navigate(MY_PROPERTIES_ROUTE);
        return;
      }

      if (!(await savePendingChanges())) return;
      navigate(MY_PROPERTIES_ROUTE);
    } catch (requestError) {
      showRequestError(getErrorMessage(requestError, "Couldn't save the latest changes"));
      finishFailedOperation();
    }
  };

  const navigateToPublishError = (result: PropertyAdPublishValidationResult) =>
    new Promise<void>((resolve) => {
      const afterRender = () => {
        const targets = PUBLISH_ERROR_TARGETS.filter((target) =>
          target.type === 'photo' ? !result.hasEnoughPhotos : result.invalidFields.has(target.field)
        )
          .map((target) => {
            const title = document.getElementById(target.sectionTitleId);
            const section = title?.closest<HTMLElement>('section');
            const control =
              target.type === 'field'
                ? section?.querySelector<HTMLElement>(
                    `[aria-describedby~="${target.field}-error"], [id="mui-component-select-${target.field}"]`
                  ) ??
                  Array.from(document.getElementsByName(target.field)).find(
                    (element): element is HTMLElement =>
                      element instanceof HTMLElement &&
                      element.getAttribute('aria-hidden') !== 'true'
                  )
                : undefined;
            return { control, element: control ?? section };
          })
          .filter((target): target is { control: HTMLElement | undefined; element: HTMLElement } =>
            Boolean(target.element)
          )
          .sort((first, second) =>
            first.element.compareDocumentPosition(second.element) & Node.DOCUMENT_POSITION_FOLLOWING
              ? -1
              : 1
          );
        const first = targets[0];
        first?.element.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
        first?.control?.focus({ preventScroll: true });
        resolve();
      };
      if (window.requestAnimationFrame) window.requestAnimationFrame(afterRender);
      else window.setTimeout(afterRender, 0);
    });

  const validatePublish = async (currentMedia?: AdMediaType[]) => {
    const result = await validatePublishFields(currentMedia);
    if (!result.isValid && (currentMedia !== undefined || !canWaitForPendingPhotos(result))) {
      await navigateToPublishError(result);
    }
    return result;
  };

  const canWaitForPendingPhotos = (result: PropertyAdPublishValidationResult) =>
    result.areFieldsValid && !result.hasEnoughPhotos && props.hasPendingMedia();

  const handlePublishClick = async () => {
    if (operationRef.current || publishClickPendingRef.current) return;
    publishClickPendingRef.current = true;
    try {
      const result = await validatePublish();
      if (!result.isValid && !canWaitForPendingPhotos(result)) {
        if (
          result.invalidFields.size === 1 &&
          result.invalidFields.has('locationId') &&
          result.hasEnoughPhotos
        ) {
          setIsLocationNoticeOpen(true);
        }
        return;
      }
      setIsPublishConfirmationOpen(true);
    } finally {
      publishClickPendingRef.current = false;
    }
  };

  const handlePublish = async () => {
    if (!beginOperation('publish')) return;
    setIsPublishConfirmationOpen(false);

    try {
      const initialResult = await validatePublish();
      if (!initialResult.isValid && !canWaitForPendingPhotos(initialResult)) {
        finishFailedOperation();
        return;
      }
      props.onFinalizingChange?.(true);
      if (!(await savePendingChanges())) return;

      const savedMedia = await props.waitForMediaIdle();
      if (!savedMedia.ok) {
        showRequestError("Couldn't save media changes. Please try again.");
        finishFailedOperation();
        return;
      }

      if (!(await validatePublish(savedMedia.media)).isValid) {
        finishFailedOperation();
        return;
      }
      if (!(await requestSave(AdFieldStatusEnum.Published, savedMedia.media))) return;

      setPublishedTitle(getValues().title);
      setIsPublishSuccessOpen(true);
    } catch (requestError) {
      showRequestError(getErrorMessage(requestError, "Couldn't publish the listing"));
      finishFailedOperation();
    }
  };

  const isBusy = operation !== null;

  return (
    <>
      <Stack aria-label='Listing actions' component='section' spacing={1.5} width='100%'>
        <Stack
          alignItems={{ xs: 'stretch', sm: 'center' }}
          direction={{ xs: 'column', sm: 'row' }}
          flexWrap={{ sm: 'wrap' }}
          gap={2.5}
        >
          <MyButton
            data={{
              buttonName: operation === 'publish' ? 'Publishing...' : 'Publish',
              buttonType: 'button',
              customWidth: '155px',
              variant: 'contained',
            }}
            disabled={isBusy}
            onClick={() => void handlePublishClick()}
          />
          <MyButton
            data={{
              buttonName: operation === 'draft' ? 'Saving...' : 'Save as a draft',
              buttonType: 'button',
              customWidth: '242px',
              styleType: 'cancel',
              variant: 'outlined',
            }}
            disabled={isBusy}
            onClick={() => void handleSaveDraft()}
          />
          <MyButton
            data={{
              buttonName: 'Cancel',
              buttonType: 'button',
              customWidth: '157px',
              styleType: 'cancel',
              variant: 'outlined',
            }}
            disabled={isBusy}
            onClick={() => void handleCancel()}
          />
        </Stack>
        {requestError && (
          <Typography color='error' component='p' role='alert' variant='body2'>
            {requestError}
          </Typography>
        )}
      </Stack>

      <MyDialog
        dialogTitle='Save Changes:'
        onClose={() => setIsPublishConfirmationOpen(false)}
        open={isPublishConfirmationOpen}
        width='750'
      >
        <Stack alignItems='center' spacing={3} textAlign='center'>
          <Typography>Confirm your updated information.</Typography>
          <Stack
            alignItems='center'
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent='center'
            spacing={4}
            width='100%'
          >
            <Box maxWidth='226px' width='100%'>
              <MyButton
                data={{
                  buttonId: 'id_button_confirm_create_edit_ad',
                  buttonName: 'Confirm',
                  buttonType: 'submit',
                  className: 'className_button_confirm_create_edit_ad',
                  customWidth: '226px',
                  sx: { maxWidth: '100%' },
                  variant: 'contained',
                }}
                disabled={isBusy}
                onClick={() => void handlePublish()}
              />
            </Box>
            <Box maxWidth='226px' width='100%'>
              <MyButton
                data={{
                  buttonName: 'Cancel',
                  buttonType: 'button',
                  customWidth: '226px',
                  styleType: 'cancel',
                  sx: { maxWidth: '100%' },
                  variant: 'outlined',
                }}
                disabled={isBusy}
                onClick={() => setIsPublishConfirmationOpen(false)}
              />
            </Box>
          </Stack>
        </Stack>
      </MyDialog>

      <MyDialog
        dialogTitle='Verification of location data'
        onClose={() => setIsLocationNoticeOpen(false)}
        open={isLocationNoticeOpen}
        width='750'
      >
        <Stack alignItems='center' spacing={3} textAlign='center'>
          <Typography>
            Your location is being clarified. You can save your listing as a draft. As soon as the
            administrator confirms the location, we will notify you by email, and you can publish
            your listing.
          </Typography>
          <Box maxWidth='351px' width='100%'>
            <MyButton
              data={{
                buttonName: 'Confirm',
                buttonType: 'button',
                customWidth: '351px',
                sx: { maxWidth: '100%' },
                variant: 'contained',
              }}
              onClick={() => setIsLocationNoticeOpen(false)}
            />
          </Box>
        </Stack>
      </MyDialog>

      <MyDialog
        dialogTitle='Your list has been successfully published'
        onClose={() => navigate(MY_PROPERTIES_ROUTE)}
        open={isPublishSuccessOpen}
        width='750'
      >
        <Stack alignItems='center' spacing={3} textAlign='center'>
          <Typography>
            Your listing{' '}
            {publishedTitle && (
              <Typography color='#1650ff' component='strong'>
                &quot;{publishedTitle}&quot;
              </Typography>
            )}{' '}
            has been successfully posted in the system and is available for other users to view.{' '}
            Your listing will be published for a{' '}
            <Typography color='#1650ff' component='strong'>
              period of 30 days.
            </Typography>{' '}
            You can update it through the{' '}
            <Typography color='#1650ff' component='strong'>
              &quot;My properties&quot;
            </Typography>{' '}
            section by clicking the{' '}
            <Typography color='#1650ff' component='strong'>
              &quot;Update&quot;
            </Typography>{' '}
            button.
          </Typography>
          <Box maxWidth='351px' width='100%'>
            <MyButton
              data={{
                buttonName: 'Confirm',
                buttonType: 'button',
                customWidth: '351px',
                sx: { maxWidth: '100%' },
                variant: 'contained',
              }}
              onClick={() => navigate(MY_PROPERTIES_ROUTE)}
            />
          </Box>
        </Stack>
      </MyDialog>
    </>
  );
};
